import { ChangeEvent, default as React, useEffect, useState } from 'react';
import {
  DevHubAlert,
  DevHubBox,
  DevHubButton,
  DevHubCircularProgress,
  DevHubDynamicIcon,
  DevHubFilter,
  DevHubGrid,
  DevHubIconButton,
  DevHubInputAdornment,
  DevHubTextField,
  DevHubTooltip,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { gql, useLazyQuery } from '@apollo/client';
import { MyApp } from './types/myAppTypes';
import { Progress } from '@backstage/core-components';
import useDebounce from 'react-use/lib/useDebounce';
import FunctionalAreaSelect from '../common/FunctionalAreaSelect';
import { FunctionalArea } from '../insights/types/InsightsTypes';
import CSVExport from './CSVExport';

const GetApplicationsQuery = gql`
  query GetApplications(
    $filter: ResourceCatalogApplicationFilter
    $pagination: PaginationInput
  ) {
    resources {
      applicationsPaged(pagination: $pagination, filter: $filter) {
        totalCount
        applications {
          applicationId
          appGroup
          serviceDomain
          description
          informationClassificationPolicy {
            name
          }
          name
          owner {
            displayName
          }
          roles {
            displayName
          }
          status
          updatedAt
          corporateEntity
        }
      }
    }
  }
`;

enum SearchType {
  NAME_OR_APP_ID = 'Name or App ID',
  APP_GROUP = 'App Group',
  SERVICE_DOMAIN = 'Service Domain',
  FUNCTIONAL_AREA = 'Functional Area',
}

type Props = {
  setSearchResults: React.Dispatch<React.SetStateAction<MyApp[]>>;
  searchResults: MyApp[];
  starMode: boolean;
  selectedStatuses: string[];
  corpEntities: string[];
};

/**
 * AppSearch searches for an application in fusion based on the name
 * @param props Props of the component
 * @param props.setApps Set applications on parent to search results
 */
const AppSearch = (props: Props) => {
  const [searchType, setSearchType] = useState<SearchType>(
    SearchType.NAME_OR_APP_ID,
  );
  const [corpEntities, setCorpEntities] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchExecuted, setSearchExecuted] = useState(false);
  const [functionalArea, setFunctionalArea] = useState<FunctionalArea>(
    {} as FunctionalArea,
  );
  const [queryFilterState, setQueryFilterState] = useState<
    Record<string, string>
  >({});
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [errorOccurred, setErrorOccurred] = useState(false);
  const [totalAppCount, setTotalAppCount] = useState(0);

  useEffect(() => {
    setCorpEntities(props.corpEntities);
  }, [props.corpEntities]);

  const [getApplications, { loading, error }] = useLazyQuery(
    GetApplicationsQuery,
    {
      variables: {
        filter: {
          corporateEntity: props.corpEntities,
        },
        pagination: {
          limit: 100,
        },
      },
      errorPolicy: 'all',
    },
  );

  const isStarred = (id: number): boolean => {
    const currentArray = localStorage.getItem('starredApplications');
    const starredAppIds = currentArray ? JSON.parse(currentArray) : [];
    return starredAppIds.includes(id);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSearchExecuted(false);
    props.setSearchResults([]);
  };

  useDebounce(
    () => {
      let trimmedValue = searchTerm?.trim();

      if (searchType === SearchType.FUNCTIONAL_AREA) {
        trimmedValue = functionalArea?.name ?? '';
      }

      if (trimmedValue.length >= 3) {
        let queryFilter: Record<string, string> = {};

        switch (searchType) {
          case SearchType.NAME_OR_APP_ID:
            queryFilter = { applicationName: `*${trimmedValue}*` };
            break;
          case SearchType.APP_GROUP:
            queryFilter = { applicationGroup: `*${trimmedValue}*` };
            break;
          case SearchType.SERVICE_DOMAIN:
            queryFilter = { serviceDomain: `*${trimmedValue}*` };
            break;
          case SearchType.FUNCTIONAL_AREA:
            queryFilter = { functionalArea: `${trimmedValue}` };
            break;
          default:
            break;
        }

        setQueryFilterState(queryFilter);

        getApplications({
          variables: {
            filter: {
              corporateEntity: props.corpEntities,
              ...queryFilter,
            },
            pagination: {
              limit: 100,
            },
          },
        }).then(appResults => {
          // Get appResults and add isStarred to each app
          const modifiedResults =
            appResults?.data?.resources?.applicationsPaged.applications?.map(
              (app: MyApp) => {
                return {
                  ...app,
                  isStarred: isStarred(app.applicationId),
                  url: `/catalog/default/system/${app.applicationId}`,
                };
              },
            );
          props.setSearchResults(modifiedResults);
          setTotalAppCount(
            appResults?.data?.resources?.applicationsPaged.totalCount,
          );
        });
        setSearchExecuted(true);
      } else if (trimmedValue === '') {
        clearSearch();
        return;
      }
    },
    500,
    [searchTerm, searchType, functionalArea, corpEntities],
  );

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchExecuted(false);
    setSearchTerm(value);
  };

  const renderAlertMessage = () => {
    const totalCount = props?.searchResults?.length || 0;
    const starredCount =
      props?.searchResults?.filter(app => app?.isStarred).length || 0;
    const statusFilteredCount =
      props?.searchResults?.filter(app =>
        props?.selectedStatuses.includes(app?.status),
      ).length || 0;

    const applicationText = starredCount === 1 ? 'application' : 'applications';
    const resultsText = totalCount === 1 ? 'result' : 'results';

    if (props?.starMode && totalCount > 0) {
      if (starredCount === 0) {
        return `No starred results that match "${searchTerm}" found`;
      }
      return `Showing ${starredCount} starred ${applicationText} of ${totalCount} results for "${searchTerm}"`;
    }

    if (props?.selectedStatuses.length > 0) {
      return `Showing ${statusFilteredCount} ${resultsText} for "${searchTerm}", when searching by ${searchType} - ${starredCount} starred`;
    }

    return `Showing ${totalCount} ${resultsText} for "${searchTerm}", when searching by ${searchType} - ${starredCount} starred`;
  };

  const handleSearchTypeChange = (event: string) => {
    setSearchExecuted(false);
    setSearchType(event as SearchType);
  };

  const handleClick = () => {
    setIsModalOpen(true);
  };

  const AppExportButton = () => {
    return errorOccurred ? (
      <DevHubTooltip title="Error occured while fetching Applications">
        <span>
          <DevHubDynamicIcon
            style={{ marginLeft: '0.5rem' }}
            iconName="ReportProblem"
            color="error"
            fontSize="large"
          />
        </span>
      </DevHubTooltip>
    ) : (
      <React.Fragment>
        {isExporting ? (
          <DevHubCircularProgress />
        ) : (
          <DevHubButton
            variant="contained"
            color="primary"
            onClick={handleClick}
            disabled={
              typeof props?.searchResults?.length === 'undefined' ||
              props?.searchResults?.length === 0
            }
          >
            Export
          </DevHubButton>
        )}
      </React.Fragment>
    );
  };

  return (
    <DevHubBox>
      <DevHubGrid container padding="small" justifyContent="flex-start">
        <DevHubGrid item xs={12} md={5}>
          <DevHubTypography variant="h1" display="inline" fontColor="red">
            Find
          </DevHubTypography>{' '}
          <DevHubTypography variant="h1" display="inline">
            Applications
          </DevHubTypography>
        </DevHubGrid>
        <DevHubGrid item xs={12} md={7}>
          <DevHubBox display="flex" margin="medium" justifyContent="flex-end">
            {searchType === 'Functional Area' ? (
              <FunctionalAreaSelect
                noMargin
                selectedFunctionalArea={functionalArea}
                setFunctionalArea={setFunctionalArea}
              />
            ) : (
              <DevHubTextField
                size="small"
                id="app-search"
                label={`Search By ${searchType}`}
                variant="outlined"
                noMargin
                onChange={onChange}
                value={searchTerm}
                style={{ width: '450px' }}
                InputProps={{
                  className: 'searchField',
                  endAdornment: (
                    <DevHubInputAdornment position="end">
                      <DevHubIconButton onClick={clearSearch}>
                        <DevHubDynamicIcon iconName="Clear" />
                      </DevHubIconButton>
                    </DevHubInputAdornment>
                  ),
                }}
              />
            )}
            <DevHubFilter
              selectedItems={[searchType]}
              title="Filters"
              list={Object.entries(SearchType).map(([_key, value]) => value)}
              height="large"
              handleChange={handleSearchTypeChange}
            />
          </DevHubBox>
        </DevHubGrid>
      </DevHubGrid>
      {loading && <Progress />}
      {error && (
        <div>
          <DevHubAlert variant="filled" severity="error">
            Error loading data
          </DevHubAlert>
        </div>
      )}
      {!loading &&
        searchExecuted &&
        (props?.searchResults?.length === 0 ? (
          <DevHubAlert variant="outlined" severity="error">
            No results found for "{searchTerm}"
          </DevHubAlert>
        ) : (
          <DevHubGrid item xs={12}>
            <DevHubBox display="flex" justifyContent="space-between">
              <DevHubGrid item xs={12}>
                <DevHubAlert variant="outlined" severity="success">
                  {renderAlertMessage()}
                </DevHubAlert>
              </DevHubGrid>
              <DevHubBox mt={1} lrPadding="small">
                <DevHubGrid item xs={1}>
                  <AppExportButton />
                </DevHubGrid>
              </DevHubBox>
            </DevHubBox>
            {isExporting && !errorOccurred && (
              <DevHubBox my={1}>
                <DevHubAlert variant="filled" severity="info">
                  Exporting Application Data to CSV. Do not close or switch the
                  window
                </DevHubAlert>
              </DevHubBox>
            )}
          </DevHubGrid>
        ))}
      <CSVExport
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        queryFilterState={queryFilterState}
        GetApplicationsQuery={GetApplicationsQuery}
        setIsExporting={setIsExporting}
        setErrorOccurred={setErrorOccurred}
        totalAppCount={totalAppCount}
      />
    </DevHubBox>
  );
};

export default AppSearch;
